import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ChatCircleDots, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track, USAGE_EVENTS } from "@/lib/usage";

/** V2 Simulateur — Bloc "Rappels demandés", EN TÊTE du Commando. Safe = Silence. */

const CRENEAU = { asap: "Dès que possible", matin: "Matin", midi: "Midi", apres_midi: "Après-midi", fin_journee: "Fin de journée" };
const FAIS = { vert: "🟢", orange: "🟠", rouge: "🔴" };
const PM = { en_reflexion: "Réflexion", en_recherche: "Recherche", offre_en_cours: "Offre en cours" };

function since(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `il y a ${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 48) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
}

function RappelItem({ r, onHandled }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const phone = String(r.client_phone || "").replace(/[^\d+]/g, "");
  const hasPhone = phone.length >= 8;
  const wa = phone.replace(/^\+/, "").replace(/^0/, "33");
  const handled = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/dossiers/${r.id}/callback-handled`);
      track(USAGE_EVENTS.COMMANDO_RELANCE, { dossier_id: r.id, kind: "callback" });
      toast.success("Rappel effectué");
      onHandled(r.id);
    } catch { toast.error("Impossible de clôturer la demande"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 cursor-pointer hover:bg-amber-50 transition" onClick={() => navigate(`/dossier/${r.id}`)} data-testid={`rappel-item-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-slate-900 text-[14px] truncate">{r.nom}</div>
          <div className="text-[11.5px] text-slate-600 mt-0.5 flex flex-wrap gap-x-2">
            <span className="font-bold text-amber-800" data-testid={`rappel-since-${r.id}`}>📞 Demandé {since(r.callback_requested_at)}</span>
            {r.callback_preference && <span>· {CRENEAU[r.callback_preference] || r.callback_preference}</span>}
            {r.callback_channel === "email" && <span>· préfère l'email</span>}
          </div>
          {(r.source || r.attribution) && (
            <div className="text-[11px] text-violet-700 mt-0.5 flex flex-wrap gap-x-2" data-testid={`rappel-source-${r.id}`}>
              <span className="font-bold">📣 {r.source || "Simulateur"}</span>
              {r.attribution?.utm_campaign && <span>· {r.attribution.utm_campaign}</span>}
              {(r.attribution?.campaign_id || r.attribution?.adset_id || r.attribution?.ad_id) && (
                <span className="text-violet-500">· ids {[r.attribution.campaign_id, r.attribution.adset_id, r.attribution.ad_id].filter(Boolean).join(" / ")}</span>
              )}
            </div>
          )}
          {r.possible_duplicates_count > 0 && (
            <div className="text-[11px] font-bold text-amber-800 mt-0.5" data-testid={`rappel-dup-${r.id}`}>⚠️ {r.possible_duplicates_count} correspondance(s) possible(s) — vérifier avant d'appeler</div>
          )}
          {r.client_phone_display && <div className="text-[11.5px] text-slate-700 font-bold mt-0.5" data-testid={`rappel-phone-${r.id}`}>{r.client_phone_display}</div>}
          <div className="text-[11.5px] text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
            {r.sim_faisabilite && <span>{FAIS[r.sim_faisabilite]} Faisabilité</span>}
            {r.project_maturity && <span>· {PM[r.project_maturity]}</span>}
            {r.etape === "compromis" && <span>· Compromis</span>}
            {(r.sim_flags || []).length > 0 && <span className="text-slate-400">· {r.sim_flags.length} point(s) d'attention</span>}
          </div>
        </div>
        <ArrowRight size={14} className="text-slate-400 mt-1 shrink-0" />
      </div>
      <div className="flex items-center gap-2 mt-2.5">
        <a href={hasPhone ? `tel:${phone}` : undefined} onClick={(e) => { e.stopPropagation(); if (hasPhone) track(USAGE_EVENTS.COMMANDO_APPEL, { dossier_id: r.id }); }}
          className={`flex-1 inline-flex items-center justify-center gap-1 min-h-[38px] rounded-lg text-[12.5px] font-extrabold ${hasPhone ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-400 pointer-events-none"}`} data-testid={`rappel-call-${r.id}`}>
          <Phone size={14} weight="fill" /> Appeler
        </a>
        <a href={hasPhone ? `https://wa.me/${wa}` : undefined} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center justify-center min-h-[38px] px-3 rounded-lg text-[12.5px] font-extrabold ${hasPhone ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-400 pointer-events-none"}`} data-testid={`rappel-wa-${r.id}`}>
          <ChatCircleDots size={14} weight="fill" />
        </a>
        <button type="button" onClick={handled} disabled={busy}
          className="inline-flex items-center justify-center gap-1 min-h-[38px] px-3 rounded-lg text-[12.5px] font-extrabold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid={`rappel-handled-${r.id}`}>
          <CheckCircle size={14} weight="fill" /> Rappel effectué
        </button>
      </div>
    </div>
  );
}

export default function RappelsDemandes({ items = [] }) {
  const [hidden, setHidden] = useState(new Set());
  const list = items.filter((r) => !hidden.has(r.id));
  if (list.length === 0) return null;
  return (
    <section className="card-base p-4 mb-4 border-2 border-amber-300" data-testid="commando-rappels-demandes">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-extrabold text-amber-700">Priorité absolue</div>
          <h2 className="font-extrabold text-slate-900 text-[16px] leading-tight">📞 Rappels demandés</h2>
        </div>
        <span className="text-[12px] font-extrabold text-amber-800 bg-amber-100 rounded-full px-2.5 py-1" data-testid="commando-rappels-count">{list.length}</span>
      </div>
      <div className="space-y-2.5">
        {list.map((r) => <RappelItem key={r.id} r={r} onHandled={(id) => setHidden((s) => new Set(s).add(id))} />)}
      </div>
    </section>
  );
}
